import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { ProgressBar } from '../components/ProgressBar';
import { SurveyOption } from '../components/SurveyOption';
import { PhotoOption } from '../components/PhotoOption';
import { Footer } from '../components/Footer';
import { Lock, CheckCircle2, Check } from 'lucide-react';
// Survey Data Structure
const questions = [{
  id: 1,
  text: 'I am...',
  step: 1,
  type: 'photo' as const,
  showIntro: true,
  options: [{
    label: 'Male',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=600&fit=crop',
    gradient: 'from-blue-600/70 to-indigo-900/70'
  }, {
    label: 'Female',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=600&fit=crop',
    gradient: 'from-pink-600/70 to-purple-900/70'
  }]
}, {
  id: 2,
  text: 'How old are you?',
  step: 1,
  type: 'emoji' as const,
  options: [{
    emoji: '🌱',
    text: 'Below 18'
  }, {
    emoji: '🌟',
    text: '18-25'
  }, {
    emoji: '✨',
    text: '25-40'
  }, {
    emoji: '🌺',
    text: '40+'
  }]
}, {
  id: 3,
  text: 'Do you believe in spirituality?',
  step: 1,
  type: 'emoji' as const,
  options: [{
    emoji: '✅',
    text: 'Yes'
  }, {
    emoji: '❌',
    text: 'No'
  }, {
    emoji: '🤷',
    text: 'Not Sure'
  }]
}, {
  id: 4,
  text: 'How can our psychic assist you?',
  step: 1,
  type: 'emoji' as const,
  options: [{
    emoji: '💔',
    text: 'Find out about my ex'
  }, {
    emoji: '👩‍❤️‍👨',
    text: 'Give advice for my love life'
  }, {
    emoji: '✨',
    text: 'Give guidance on my future'
  }, {
    emoji: '💼',
    text: 'Explore my career path'
  }, {
    emoji: '🧿',
    text: 'Give clarity through spirit guidance'
  }, {
    emoji: '🔮',
    text: 'All of the above'
  }]
}, {
  id: 5,
  text: 'Are you satisfied with how things are going in your life?',
  step: 2,
  type: 'emoji' as const,
  options: [{
    emoji: '❌',
    text: 'No'
  }, {
    emoji: '✅',
    text: 'Yes'
  }, {
    emoji: '🤷',
    text: 'Not Sure'
  }]
}, {
  id: 6,
  text: 'Have you ever received a psychic reading?',
  step: 2,
  type: 'emoji' as const,
  options: [{
    emoji: '✅',
    text: 'Yes'
  }, {
    emoji: '❌',
    text: 'No'
  }]
}];
const loadingSteps = [{
  text: 'Analyzing your answers',
  delay: 0
}, {
  text: 'Fetching a list of available psychics',
  delay: 1500
}, {
  text: 'Matching you to the best psychics',
  delay: 3000
}, {
  text: 'Personalizing your experience',
  delay: 4500
}];
export function SurveyPage() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [direction, setDirection] = useState(1);
  const [showLoading, setShowLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const showIntro = currentQuestion?.showIntro;
  useEffect(() => {
    if (showLoading) {
      // Animate progress from 0 to 100
      const duration = 6000; // 6 seconds
      const steps = 100;
      const interval = duration / steps;
      let currentProgress = 0;
      const timer = setInterval(() => {
        currentProgress += 1;
        setProgress(currentProgress);
        if (currentProgress >= 100) {
          clearInterval(timer);
          // Show the continue button when progress reaches 100
          setTimeout(() => {
            setShowContinueButton(true);
          }, 500);
        }
      }, interval);
      // Mark steps as completed
      loadingSteps.forEach((step, index) => {
        setTimeout(() => {
          setCompletedSteps(prev => [...prev, index]);
        }, step.delay);
      });
      return () => clearInterval(timer);
    }
  }, [showLoading]);
  const handleOptionClick = (optionValue: string) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionValue
    }));
    if (!isLastQuestion) {
      setDirection(1);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 300);
    } else {
      // Show loading screen after last question
      setDirection(1);
      setTimeout(() => {
        setShowLoading(true);
      }, 300);
    }
  };
  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setDirection(-1);
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -50 : 50,
      opacity: 0
    })
  };
  if (showLoading) {
    return <div className="min-h-screen w-full bg-[#f5f1e8] text-[#2d5f5d] overflow-hidden flex flex-col relative" data-id="element-51">
        <Header onBack={() => {}} showBack={false} data-id="element-52" />

        <main className={`flex-1 flex flex-col items-center justify-center px-4 max-w-2xl mx-auto w-full transition-all duration-500 ${showContinueButton ? 'blur-sm' : ''}`} data-id="element-53">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex flex-col items-center text-center w-full" data-id="element-54">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-[#2d5f5d]" data-id="element-55">
              <span className="text-[#5a7c7a]" data-id="element-56">Great, you are all set.</span>
              <br data-id="element-57" />
              Now we are creating your profile
            </h2>

            {/* Circular Progress */}
            <div className="relative w-48 h-48 my-12" data-id="element-58">
              <svg className="w-full h-full transform -rotate-90" data-id="element-59">
                {/* Background circle */}
                <circle cx="96" cy="96" r="88" stroke="#d4af37" strokeWidth="8" fill="none" opacity="0.2" data-id="element-60" />
                {/* Progress circle */}
                <motion.circle cx="96" cy="96" r="88" stroke="#d4af37" strokeWidth="8" fill="none" strokeLinecap="round" initial={{
                strokeDasharray: '0 552'
              }} animate={{
                strokeDasharray: `${progress / 100 * 552} 552`
              }} transition={{
                duration: 0.1
              }} data-id="element-61" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-62">
                <span className="text-5xl font-bold text-[#2d5f5d]" data-id="element-63">
                  {progress}%
                </span>
              </div>
            </div>

            {/* Loading Steps */}
            <div className="w-full max-w-md space-y-3" data-id="element-64">
              {loadingSteps.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: step.delay / 1000
            }} className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 ${completedSteps.includes(index) ? 'bg-[#d4af37]/20 border border-[#d4af37]' : 'bg-[#2d5f5d]/5 border border-transparent'}`} data-id="element-65">
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${completedSteps.includes(index) ? 'bg-[#d4af37] text-white' : 'bg-[#2d5f5d]/20 text-[#2d5f5d]'}`} data-id="element-66">
                    {completedSteps.includes(index) && <Check className="w-4 h-4" data-id="element-67" />}
                  </div>
                  <span className={`text-sm md:text-base font-medium transition-colors duration-300 ${completedSteps.includes(index) ? 'text-[#2d5f5d]' : 'text-[#5a7c7a]'}`} data-id="element-68">
                    {step.text}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </main>

        {/* Continue Button Overlay - Pre-rendered but hidden */}
        <AnimatePresence data-id="element-69">
          {showContinueButton && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 flex items-center justify-center z-50 bg-black/20" data-id="element-70">
              <motion.a href="https://trk.topreviewspicks.com/click" initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.3
          }} className="px-10 py-3 bg-[#e67e22] text-white font-semibold text-3xl rounded-full shadow-lg hover:bg-[#d4721f] transition-all duration-300 hover:shadow-xl" data-id="element-71">
                Continue
              </motion.a>
            </motion.div>}
        </AnimatePresence>

        <Footer data-id="element-72" />
      </div>;
  }
  return <div className="min-h-screen w-full bg-[#f5f1e8] text-[#2d5f5d] overflow-hidden flex flex-col" data-id="element-73">
      {/* Decorative top gradient bar */}

      <Header onBack={handleBack} showBack={currentQuestionIndex > 0} data-id="element-74" />

      <main className="flex-1 flex flex-col items-center px-2 pt-4 pb-4 max-w-2xl mx-auto w-full" data-id="element-75">
        {/* Progress Section - Hide on intro screen */}
        {!showIntro && currentQuestion && <div className="w-full mb-2" data-id="element-76">
            <ProgressBar currentQuestion={currentQuestionIndex + 1} totalQuestions={26} data-id="element-77" />
          </div>}

        {/* Content Section */}
        <div className="w-full relative min-h-[400px]" data-id="element-78">
          <AnimatePresence mode="wait" custom={direction} data-id="element-79">
            <motion.div key={currentQuestionIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="w-full" data-id="element-80">
              {/* Intro Content - Only on first screen */}
              {showIntro && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="flex flex-col items-center text-center" data-id="element-81">
                  <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight text-[#2d5f5d]" data-id="element-82">
                    Accurate, personalized
                    <br data-id="element-83" />
                    readings from top psychics
                  </h1>

                  <p className="text-[#5a7c7a] text-base md:text-lg mb-6 max-w-xl" data-id="element-84">
                    Start with our 2-min quiz to get matched you with a psychic
                    who best meets your needs
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-3 mb-8" data-id="element-85">
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[#d4af37] shadow-sm" data-id="element-86">
                      <Lock className="w-4 h-4 text-[#e67e22]" data-id="element-87" />
                      <span className="text-sm text-[#2d5f5d] font-medium" data-id="element-88">
                        Guaranteed privacy
                      </span>
                    </div>

                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[#d4af37] shadow-sm" data-id="element-89">
                      <CheckCircle2 className="w-4 h-4 text-[#e67e22]" data-id="element-90" />
                      <span className="text-sm text-[#2d5f5d] font-medium" data-id="element-91">
                        Verified psychics
                      </span>
                    </div>
                  </div>
                </motion.div>}

              <h2 className="text-xl md:text-2xl font-bold text-center mb-8 px-4 leading-relaxed text-[#2d5f5d]" data-id="element-92">
                {currentQuestion.text}
              </h2>

              {currentQuestion.type === 'photo' ? <div className="flex flex-wrap items-center justify-center gap-6 w-full" data-id="element-93">
                  {currentQuestion.options.map((option, idx) => <motion.div key={option.label} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.1
              }} data-id="element-94">
                      <PhotoOption image={option.image} label={option.label} gradient={option.gradient} onClick={() => handleOptionClick(option.label)} selected={answers[currentQuestion.id] === option.label} data-id="element-95" />
                    </motion.div>)}
                </div> : <div className="flex flex-col gap-1 w-full" data-id="element-96">
                  {currentQuestion.options.map((option, idx) => <motion.div key={option.text} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.05
              }} data-id="element-97">
                      <SurveyOption emoji={option.emoji!} text={option.text!} onClick={() => handleOptionClick(option.text!)} selected={answers[currentQuestion.id] === option.text} data-id="element-98" />
                    </motion.div>)}
                </div>}

              {currentQuestion.type === 'photo' && <p className="text-center text-sm text-[#5a7c7a] mt-8 max-w-md mx-auto" data-id="element-99">
                  Our algorithm will match you to the top psychic who will
                  provide guidance and the most accurate answers.
                </p>}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <Footer data-id="element-100" />
    </div>;
}